import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Alert,
    Chip,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CheckCircle, Cancel, UploadFile } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { readTranscript, retryAnalysis, uploadLesson } from '../../services/lessonService';
import { MAX_BYTES, checkDocx, checkTranscript, readText, secondsFor } from './transcriptCheck';

const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const Section = ({ n, title, aside, children }) => (
    <Paper component="section" aria-labelledby={`step-${n}`} sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
            <Box aria-hidden sx={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: '#fff', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, alignSelf: 'center' }}>{n}</Box>
            <Typography id={`step-${n}`} variant="h5" component="h2">{title}</Typography>
            {aside && <Typography variant="caption" color="text.secondary">{aside}</Typography>}
        </Box>
        {children}
    </Paper>
);

// Frame 1–3: the transcript file, read and checked in the browser; nothing is sent yet
const FileStep = ({ file, check, reading, onPick, onClear, error }) => {
    const input = useRef(null);
    const [dragging, setDragging] = useState(false);
    const shown = check?.problems?.slice(0, 10) || [];

    return (
        <Section n={1} title="Transcript file">
            {!file ? (
                <Box
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => { e.preventDefault(); setDragging(false); onPick(e.dataTransfer.files?.[0]); }}
                    sx={{ border: '2px dashed', borderColor: dragging ? 'primary.main' : 'divider', bgcolor: dragging ? (t) => alpha(t.palette.primary.main, 0.05) : 'background.default', borderRadius: 2, p: { xs: 3, sm: 4 }, textAlign: 'center', transition: 'border-color 150ms, background-color 150ms' }}
                >
                    <UploadFile sx={{ fontSize: 36, color: 'text.secondary' }} aria-hidden />
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        Drop a transcript file here, or{' '}
                        <Button variant="text" onClick={() => input.current?.click()} sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline', fontSize: 'inherit' }}>browse</Button>
                    </Typography>
                    <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1 }}>
                        CSV or TXT with columns number, speaker, utterance, or a Word (.docx) transcript · speaker T (teacher), S (student) or a student’s initials · up to 5 MB
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: reading ? 'divider' : check?.ok ? 'success.light' : 'error.light', bgcolor: (t) => (reading ? 'transparent' : alpha(check?.ok ? t.palette.success.main : t.palette.error.main, 0.05)) }}>
                    {reading ? <UploadFile color="action" aria-hidden /> : check?.ok ? <CheckCircle color="success" aria-label="Checked" /> : <Cancel color="error" aria-label="Has problems" />}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{file.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {reading
                                ? 'Reading the Word file…'
                                : check?.fileError
                                ? check.fileError
                                : check?.ok
                                    ? `${check.utterances} utterances · Teacher ${check.teacher} · Student ${check.student} · no problems found`
                                    : `${check?.problems.length} ${check?.problems.length === 1 ? 'row needs' : 'rows need'} fixing before this lesson can be analysed`}
                        </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={onClear}>Replace</Button>
                </Box>
            )}
            <input ref={input} type="file" accept=".csv,.txt,.docx,text/csv,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document" hidden aria-label="Choose a transcript file" onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ''; }} />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {check?.ok && (
                <Box sx={{ mt: 2, overflowX: 'auto' }}>
                    <Typography variant="caption" color="text.secondary">Preview: first 5 rows as they will be read</Typography>
                    <Table size="small" aria-label="Preview" sx={{ mt: 0.5, minWidth: 480 }}>
                        <TableHead>
                            <TableRow><TableCell>#</TableCell><TableCell>Speaker (file → read as)</TableCell><TableCell>Utterance</TableCell></TableRow>
                        </TableHead>
                        <TableBody>
                            {check.preview.map((r) => (
                                <TableRow key={r.row}>
                                    <TableCell>{r.row}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.raw} → {r.roleLabel}</TableCell>
                                    <TableCell>{r.text}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {check.initials?.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Read as students’ initials: {check.initials.join(', ')}. If one of these is the teacher or another adult, change it to T in your file and choose it again.
                        </Typography>
                    )}
                </Box>
            )}
            {check && !check.ok && !check.fileError && (
                <Box sx={{ mt: 2 }}>
                    <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 0.5 }}>
                        {shown.map((p) => (
                            <Box component="li" key={p.row} sx={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>Row {p.row}</Typography>
                                <Typography variant="body2">{p.message}</Typography>
                            </Box>
                        ))}
                    </Box>
                    {check.problems.length > shown.length && (
                        <Typography variant="caption" color="text.secondary">… and {check.problems.length - shown.length} more</Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Fix these rows in your file and choose it again. Nothing has been uploaded yet.</Typography>
                </Box>
            )}
            <Link href="/sample-lesson.csv" download sx={{ display: 'inline-block', mt: 1.5, fontSize: '0.875rem', fontWeight: 600 }}>Download a sample file</Link>
        </Section>
    );
};

// Frame 4: the request stays open until the analysis is done; the bar runs on the page's estimate
const Analysing = ({ title, file, utterances, shared }) => {
    const total = secondsFor(utterances);
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setElapsed((e) => e + 1), 1000);
        return () => clearInterval(t);
    }, []);
    const left = Math.max(0, total - elapsed);
    return (
        <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 640 }} aria-live="polite">
            <Typography variant="h4" component="h2">Analysing {title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Coding {utterances} utterances · {left > 0 ? `about ${left} ${left === 1 ? 'second' : 'seconds'} left` : 'Almost done'}
            </Typography>
            <LinearProgress variant="determinate" value={Math.min(96, (elapsed / total) * 100)} sx={{ mt: 2, height: 8, borderRadius: 999 }} aria-label="Analysis progress" />
            {typeof shared === 'boolean' && <Typography variant="body2" sx={{ mt: 2 }}>Research use: {shared ? 'Shared' : 'Not shared'}</Typography>}
            <Typography variant="body2" color="text.secondary">{file} · uploaded</Typography>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 2 }}>Keep this page open. The analysis opens when it is done.</Typography>
        </Paper>
    );
};

// The researcher's ready state (wf-research-upload-2): a researcher has no My lessons, so the page
// stays here and points to the lesson in the Lessons library. No codes, charts or confidences.
const ResearchReady = ({ done, about, utterances, onAnother }) => (
    <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 640 }} aria-live="polite">
        <Chip size="small" label="Ready" sx={{ borderRadius: 999, fontWeight: 600, color: '#1F6B31', bgcolor: '#E6F4EA' }} />
        <Typography variant="h4" component="h2" sx={{ mt: 1.5 }}>{[done.code, about.name.trim()].filter(Boolean).join(' · ') || 'Your lesson'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {[`${utterances} utterances analysed`, 'Research use', about.subject.trim(), about.gradeLevel && `Grade ${about.gradeLevel}`].filter(Boolean).join(' · ')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
            It is in the Lessons library under its code. To have it coded, add it to a run from Runs › New run from lessons.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
            <Button variant="contained" component={RouterLink} to={`/research/lessons/${encodeURIComponent(done.lessonId)}`}>Open in Lessons</Button>
            <Button variant="outlined" onClick={onAnother}>Upload another</Button>
        </Box>
    </Paper>
);

// /upload (wf-teacher-upload 1–6, YES-264): one page, three sections. The research-use choice has no
// default: Analyse stays off until it is made (YES-204). Researchers upload under a protocol and send
// no research-use choice (call map section 18).
const UploadLesson = () => {
    const navigate = useNavigate();
    const { can } = useAuth();
    const researcher = can('lesson.research');
    const [file, setFile] = useState(null);
    const [check, setCheck] = useState(null);
    const [reading, setReading] = useState(false);
    // The latest pick: a Word file read after Replace or another pick is dropped
    const picked = useRef(0);
    const [fileError, setFileError] = useState(null);
    const [about, setAbout] = useState({ name: '', subject: '', gradeLevel: '' });
    const [researchUse, setResearchUse] = useState(null);
    const [phase, setPhase] = useState('form');
    const [failure, setFailure] = useState(null);
    const [done, setDone] = useState(null);

    // A teacher goes to the analysis; a researcher stays for the ready state (call map section 18)
    const finish = (result) => {
        if (researcher) { setDone(result); setPhase('ready'); } else navigate(`/lessons/${encodeURIComponent(result.lessonId)}`);
    };
    const reset = () => {
        setFile(null); setCheck(null); setFileError(null); setAbout({ name: '', subject: '', gradeLevel: '' }); setDone(null); setFailure(null); setPhase('form');
    };

    // Replace: back to the empty drop zone; the lesson details and research-use choice stay
    const clearFile = () => {
        picked.current += 1;
        setFile(null); setCheck(null); setFileError(null); setReading(false);
    };

    const pick = async (chosen) => {
        setFileError(null);
        if (!chosen) return;
        const id = ++picked.current;
        const current = () => id === picked.current;
        if (!/\.(csv|txt|docx)$/i.test(chosen.name)) { setFileError('Choose a CSV, TXT or Word (.docx) file.'); return; }
        if (chosen.size > MAX_BYTES) { setFileError('This file is over 5 MB.'); return; }
        setFile(chosen);
        setCheck(null);
        if (!/\.docx$/i.test(chosen.name)) {
            const text = await readText(chosen);
            if (current()) setCheck(checkTranscript(text));
            return;
        }
        // A Word file is read by the server (YES-311); nothing is stored until Analyse lesson
        setReading(true);
        try {
            const read = await readTranscript(chosen);
            if (current()) setCheck(checkDocx(read));
        } catch (err) {
            if (!current()) return;
            const message = err.response?.status === 400 || err.response?.status === 503
                ? err.response.data?.error
                : null;
            setCheck({ ok: false, problems: [], fileError: message || 'The Word file could not be read. Try again, or upload the transcript as a CSV.' });
        } finally {
            if (current()) setReading(false);
        }
    };

    const choiceMade = researcher || researchUse !== null;
    const ready = Boolean(file && check?.ok && choiceMade);
    const title = about.name.trim() || file?.name;

    const analyse = async () => {
        setPhase('analysing');
        setFailure(null);
        try {
            const result = await uploadLesson({ file, ...about, researchUse: researcher ? undefined : researchUse === 'yes' });
            if (result.status === 'ready') finish(result);
            else { setFailure({ lessonId: result.lessonId }); setPhase('failed'); }
        } catch (err) {
            const status = err.response?.status;
            if (status === 400 && err.response?.data?.problems) {
                setCheck({ ok: false, problems: err.response.data.problems });
                setPhase('form');
            } else if (status === 413) {
                setFileError('This file is over 5 MB.');
                setPhase('form');
            } else {
                // Nothing came back with a lessonId: nothing was stored, the form stays filled (frame 6)
                setPhase('uploadFailed');
            }
        }
    };

    const tryAgain = async () => {
        setPhase('analysing');
        try {
            const result = await retryAnalysis(failure.lessonId);
            if (result.status === 'ready') finish(result);
            else setPhase('failed');
        } catch (err) {
            setPhase('failed');
        }
    };

    if (phase === 'analysing') {
        return (
            <Box sx={{ maxWidth: 880, mx: 'auto' }}>
                <Typography variant="h2" component="h1" sx={{ mt: { xs: 1, sm: 2 }, mb: 3 }}>Upload a lesson</Typography>
                <Analysing title={title} file={file.name} utterances={check.utterances} shared={researcher ? undefined : researchUse === 'yes'} />
            </Box>
        );
    }

    if (phase === 'ready' && done) {
        return (
            <Box sx={{ maxWidth: 880, mx: 'auto' }}>
                <Typography variant="h2" component="h1" sx={{ mt: { xs: 1, sm: 2 }, mb: 3 }}>Upload a lesson</Typography>
                <ResearchReady done={done} about={about} utterances={check.utterances} onAnother={reset} />
            </Box>
        );
    }

    if (phase === 'failed') {
        return (
            <Box sx={{ maxWidth: 880, mx: 'auto' }}>
                <Typography variant="h2" component="h1" sx={{ mt: { xs: 1, sm: 2 }, mb: 3 }}>Upload a lesson</Typography>
                <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 640 }}>
                    <Typography variant="h4" component="h2">The analysis did not finish</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        The coding service did not answer. {researcher ? 'Your lesson was saved' : 'Your lesson and your research-use choice were saved'}; nothing else is needed from you.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                        <Button variant="contained" onClick={tryAgain}>Try again</Button>
                        {researcher
                            ? <Button variant="outlined" component={RouterLink} to="/lessons">Go to Lessons</Button>
                            : <Button variant="outlined" component={RouterLink} to="/dashboard">Go to My lessons</Button>}
                    </Box>
                    <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 2 }}>
                        {[about.name.trim(), file.name, `${check.utterances} utterances`].filter(Boolean).join(' · ')}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 880, mx: 'auto' }}>
            <Typography variant="h2" component="h1" sx={{ mt: { xs: 1, sm: 2 } }}>Upload a lesson</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                {researcher
                    ? 'Add a classroom transcript to ADAS. It is analysed like a teacher’s upload and gets a lesson code, such as U-0413.'
                    : 'Get an analysis of a classroom transcript: who talks, which kinds of utterances, and how complex the reasoning is.'}
            </Typography>

            <Box sx={{ display: 'grid', gap: 2.5 }}>
                <FileStep file={file} check={check} reading={reading} onPick={pick} onClear={clearFile} error={fileError} />

                <Section n={2} title="About the lesson" aside="(optional)">
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Lesson name" placeholder="e.g. Grade 5 science, Oct 14" value={about.name} onChange={(e) => setAbout((a) => ({ ...a, name: e.target.value }))} fullWidth size="small" inputProps={{ maxLength: 120 }} />
                        </Grid>
                        <Grid item xs={8} sm={4}>
                            <TextField label="Subject" placeholder="e.g. Science" value={about.subject} onChange={(e) => setAbout((a) => ({ ...a, subject: e.target.value }))} fullWidth size="small" inputProps={{ maxLength: 60 }} />
                        </Grid>
                        <Grid item xs={4} sm={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="grade-label">Grade</InputLabel>
                                <Select labelId="grade-label" label="Grade" value={about.gradeLevel} onChange={(e) => setAbout((a) => ({ ...a, gradeLevel: e.target.value }))}>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {GRADES.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Section>

                {!researcher && (
                    <Section n={3} title="Research use" aside="(required)">
                        <RadioGroup aria-labelledby="step-3" value={researchUse || ''} onChange={(e) => setResearchUse(e.target.value)}>
                            <FormControlLabel value="yes" control={<Radio />} label="Yes, the research team may also use this lesson for research" />
                            <FormControlLabel value="no" control={<Radio />} label="No, use it only for my analysis" />
                        </RadioGroup>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Only shared lessons can be coded by researchers or used to train the model. Sharing can’t be undone: a lesson used to train the model can’t be taken back out. Your analysis is the same either way.
                        </Typography>
                    </Section>
                )}

                {phase === 'uploadFailed' && (
                    <Alert severity="error" action={<Button color="inherit" onClick={analyse}>Try again</Button>}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>The upload did not go through</Typography>
                        Check your connection and try again. Nothing was saved, so this lesson is not in {researcher ? 'Lessons' : 'My lessons'} yet.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                        {ready
                            ? `Takes about ${secondsFor(check.utterances)} seconds for ${check.utterances} utterances`
                            : check && !check.ok ? 'Fix the file to continue' : researcher ? 'Choose a file to continue' : 'Choose a file and a research-use option to continue'}
                    </Typography>
                    {phase !== 'uploadFailed' && <Button variant="contained" size="large" disabled={!ready} onClick={analyse}>Analyse lesson</Button>}
                </Box>
            </Box>
        </Box>
    );
};

export default UploadLesson;
